import React, { useCallback, useState } from 'react';
import { Button, Form } from 'antd';
import { momentForDomain } from '@common/services/moment-shadow';
import { ButtonGroup, Drawer, Modal, PrimaryButton } from '@common/components';
import { useLazyQuery, useMutation, useNavigate } from '@common/hooks';
import { buildCloseAllDialogsUrl } from '@common/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  SIMULATE_SHIFT_CHANGES_QUERY,
  UPSERT_WORKLOAD_MUTATION,
} from '@scheduling/graphql';
import {
  SetWorkloadForm,
  FormStateType,
  FormValuesType,
  utcDateAtHour,
} from './_set-workload-form';
import { PageView } from './_page-view';
import {
  transformSimulationResults,
  PreviewDataType,
} from './_transform-simulation-data-service';
import { WorkloadPreview } from './_workload-preview';

const moment = momentForDomain('scheduling', 'shifts');

export function SetWorkloadDrawer({
  open,
  onClose,
  date,
}: {
  open: boolean;
  onClose: () => void;
  date?: string;
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [formState, setFormState] = useState<FormStateType>({
    canSave: false,
    loading: false,
    hasExistingBlocks: false,
    onClear: () => {},
  });
  const [currentPage, setCurrentPage] = useState<'form' | 'preview'>('form');
  const [previewData, setPreviewData] = useState<PreviewDataType | null>(null);
  const [formValues, setFormValues] = useState<FormValuesType | null>(null);

  const [upsertWorkload, { loading: saving }] = useMutation(
    UPSERT_WORKLOAD_MUTATION,
    {
      onCompleted: (result) => {
        if (
          result?.upsertWorkload?.errors &&
          result.upsertWorkload.errors.length > 0
        ) {
          const errorMessages = result.upsertWorkload.errors
            .flatMap((e) => e.fullErrors)
            .join(', ');
          createMessage('error', errorMessages || 'Failed to set workload.');
          return;
        }
        createMessage('success', 'Workload saved. Shifts will update shortly.');
        navigate(buildCloseAllDialogsUrl());
      },
      onError: () => {
        createMessage('error', 'Failed to set workload. Please try again.');
      },
      refetchQueries: ['ShiftsCountByDay', 'ShiftGapsByDay'],
    }
  );

  const [simulate, { loading: simulating }] = useLazyQuery(
    SIMULATE_SHIFT_CHANGES_QUERY,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setPreviewData(transformSimulationResults(data.simulateShiftChanges));
        setCurrentPage('preview');
      },
      onError: () => {
        createMessage('error', 'Failed to simulate shifts. Please try again.');
      },
    }
  );

  const buildTimeBlocks = useCallback(
    (values: FormValuesType) => {
      const { boundaryHour, dayHeadcount, nightHeadcount } = values;
      const nightHour = (boundaryHour + 12) % 24;

      const dayStart = utcDateAtHour(date, boundaryHour);
      const dayEnd = utcDateAtHour(
        date,
        nightHour,
        nightHour <= boundaryHour ? 1 : 0
      );
      const nightEnd = utcDateAtHour(date, boundaryHour, 1);

      return { dayStart, dayEnd, nightEnd, dayHeadcount, nightHeadcount };
    },
    [date]
  );

  const handleContinue = useCallback(
    (values: FormValuesType) => {
      setFormValues(values);

      const { organizationUnitId, roleId } = values;
      const { dayStart, dayEnd, nightEnd, dayHeadcount, nightHeadcount } =
        buildTimeBlocks(values);

      const changes = [
        {
          operation: 'set_workload' as const,
          startDatetime: dayStart.format(),
          endDatetime: dayEnd.format(),
          organizationUnitId,
          roleId,
          quantity: dayHeadcount,
        },
        {
          operation: 'set_workload' as const,
          startDatetime: dayEnd.format(),
          endDatetime: nightEnd.format(),
          organizationUnitId,
          roleId,
          quantity: nightHeadcount,
        },
      ];

      simulate({
        variables: {
          changes,
          mode: 'set_workload' as const,
          source: 'workload_magic' as const,
        },
      });
    },
    [buildTimeBlocks, simulate]
  );

  const handleApplyWorkload = useCallback(() => {
    if (!formValues || !date) return;

    const { organizationUnitId, roleId } = formValues;
    const { dayStart, dayEnd, nightEnd, dayHeadcount, nightHeadcount } =
      buildTimeBlocks(formValues);

    upsertWorkload({
      variables: {
        organizationUnitId,
        roleId,
        blocks: [
          {
            startsAt: dayStart.format(),
            endsAt: dayEnd.format(),
            headcount: dayHeadcount,
          },
          {
            startsAt: dayEnd.format(),
            endsAt: nightEnd.format(),
            headcount: nightHeadcount,
          },
        ],
        shiftGapIdsToCancel: formValues.shiftGapIdsToCancel,
      },
    });
  }, [formValues, date, buildTimeBlocks, upsertWorkload]);

  const handleBack = useCallback(() => {
    setCurrentPage('form');
  }, []);

  const handleClose = useCallback(() => {
    form.resetFields();
    setCurrentPage('form');
    setPreviewData(null);
    setFormValues(null);
    onClose();
  }, [onClose, form]);

  const handleClear = useCallback(() => {
    Modal.confirm({
      title: 'Confirm clearing workload',
      content:
        'Clearing workload will delete all open shifts associated with the existing workload.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => formState.onClear(),
    });
  }, [formState]);

  const formattedDate = formatDate(date);

  const title =
    currentPage === 'form' ? `Set Workload - ${formattedDate}` : 'Preview';

  const footer =
    currentPage === 'form' ? (
      <ButtonGroup>
        <Button size="large" onClick={handleClose}>
          Cancel
        </Button>
        {formState.hasExistingBlocks && (
          <Button
            size="large"
            danger
            loading={formState.loading}
            onClick={handleClear}
          >
            Clear Workload
          </Button>
        )}
        <PrimaryButton
          size="large"
          loading={simulating}
          disabled={!formState.canSave}
          onClick={() => form.submit()}
        >
          Continue
        </PrimaryButton>
      </ButtonGroup>
    ) : (
      <ButtonGroup>
        <Button size="large" onClick={handleBack}>
          Back
        </Button>
        <PrimaryButton
          size="large"
          loading={saving}
          onClick={handleApplyWorkload}
        >
          Apply Workload
        </PrimaryButton>
      </ButtonGroup>
    );

  return (
    <Drawer
      open={open}
      size="lg"
      title={title}
      onClose={handleClose}
      footer={footer}
    >
      <div style={{ height: '100%' }}>
        <PageView
          currentPage={currentPage}
          formPage={
            <SetWorkloadForm
              form={form}
              date={date}
              onStateChange={setFormState}
              onContinue={handleContinue}
            />
          }
          previewPage={
            previewData ? <WorkloadPreview data={previewData} /> : null
          }
        />
      </div>
    </Drawer>
  );
}

function formatDate(date?: string) {
  return date ? moment(date).format('dddd MMMM D, YYYY') : '';
}
